import { Content, Portal, Root, Trigger } from '@radix-ui/react-dropdown-menu'
import cl from 'clsx'
import { useCallback, useState } from 'react'

import { StatusBadge } from '../StatusBadge'
import {
    ALL_STATUS_CONTENT,
    getStatusContentByType,
    type DocumentStatus,
    type DocumentType,
    type StatusContent,
} from '../data'
import Check from '../icons/check.svg?react'

import classes from './StatusSelect.module.scss'

type StatusItemProps = {
    value: DocumentStatus
    onSelect: (v: DocumentStatus) => void
    selected: boolean
    statusContent: StatusContent
    allowSelect: boolean
}

const StatusItem = ({
    value,
    onSelect,
    selected,
    statusContent,
    allowSelect,
}: StatusItemProps) => {
    const onClick = useCallback(() => {
        if (allowSelect) {
            onSelect(value)
        }
    }, [onSelect, value, allowSelect])

    const { checkStyle } = statusContent

    return (
        <StatusBadge value={value} onClick={onClick} className={classes.item}>
            <div className="grow" />
            {selected && (
                <Check className={cl(classes.check)} style={checkStyle} />
            )}
        </StatusBadge>
    )
}

export type StatusSelectProps = {
    value: DocumentStatus
    onChange: (v: DocumentStatus) => void
    documentType?: DocumentType
    classNameTrigger?: string
    classNameContent?: string
}

export const StatusSelect = ({
    value,
    onChange,
    documentType,
    classNameTrigger,
    classNameContent,
}: StatusSelectProps) => {
    const [open, setOpen] = useState(false)

    const onSelect: StatusItemProps['onSelect'] = useCallback(
        (value: DocumentStatus) => {
            onChange(value)
            setOpen(false)
        },
        [onChange]
    )

    const statusContent = documentType
        ? getStatusContentByType(documentType)
        : ALL_STATUS_CONTENT

    return (
        <Root open={open} onOpenChange={setOpen}>
            <Trigger className={cl(classes.trigger, classNameTrigger)}>
                <StatusBadge value={value} />
            </Trigger>
            <Portal>
                <Content
                    side="bottom"
                    align="start"
                    alignOffset={-11}
                    sideOffset={4}
                    className={cl(classes.content, classNameContent)}
                >
                    {Object.values(statusContent).map((itemProps) => {
                        if (itemProps.allowRenderInSelector)
                            return (
                                <StatusItem
                                    key={itemProps.value}
                                    value={itemProps.value}
                                    onSelect={onSelect}
                                    selected={itemProps.value === value}
                                    statusContent={itemProps}
                                    allowSelect={itemProps.allowSelect}
                                />
                            )
                    })}
                </Content>
            </Portal>
        </Root>
    )
}
